import { useEffect, useState } from "react";
// outsource
import { Button, Checkbox, Form, InputNumber, Modal, Radio, Row, Col, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

const PayrateBonusEditDialog = ({ editingStaff, onClose, onSave }) => {
  // -------------------------- STATE --------------------------
  const [form] = Form.useForm();
  const [category, setCategory] = useState("XRP");
  const includeXeroHours = Form.useWatch("includeXeroHours", form);

  // -------------------------- VAR --------------------------
  const isCashProfile = category === "NXRP";
  const xeroPayrateDisabled = isCashProfile;
  const xeroHoursDisabled = isCashProfile;
  const kiwiSaverDisabled = isCashProfile;
  const xeroPayDisabled = isCashProfile || includeXeroHours === true;

  // -------------------------- EFFECT --------------------------
  useEffect(() => {
    if (!editingStaff) return;
    const nextCategory = ["XRP", "NXRP"].includes(editingStaff.category) ? editingStaff.category : "XRP";
    form.setFieldsValue({
      ...editingStaff,
      category: nextCategory,
      includeXeroHours: editingStaff.includeXeroHours ?? false,
    });
    setCategory(nextCategory);
  }, [editingStaff, form]);

  // -------------------------- FUNCTION --------------------------
  const handleSave = () => {
    form.validateFields().then((values) => {
      onSave({ ...editingStaff, ...values });
    }).catch(() => { });
  };

  // -------------------------- RENDER --------------------------
  const renderNumberField = ({ label, name, disabled, suffix, required = false }) => {
    return (
      <div className="payrate-bonus-modal-row">
        <label>{label}</label>
        <div className="payrate-bonus-modal-control">
          <Form.Item
            name={name}
            className="payrate-bonus-modal-field"
            rules={required && !disabled ? [{ required: true, message: "Please input a valid number" }] : []}
          >
            <InputNumber
              min={0}
              precision={2}
              controls={false}
              disabled={disabled}
              className="payrate-bonus-modal-input"
            />
          </Form.Item>
          <span className="payrate-bonus-modal-suffix">{suffix}</span>
        </div>
      </div>
    );
  };

  const renderIncludeXeroLabel = () => {
    return (
      <span className="payrate-bonus-include-label">
        Include XERO Hours?
        <Tooltip
          overlayClassName="payrate-bonus-include-tooltip-popover"
          placement="topLeft"
          title={(
            <div className="payrate-bonus-include-tooltip">
              <div>- Include XERO Hours (Checked): Cash Pay = (Total Hours − XERO Hours) × Non-XERO Pay Rate.</div>
              <div>- Exclude XERO Hours (Unchecked): Cash Pay = (Regular Hours x Non-XERO Pay Rate) - Xero Pay</div>
            </div>
          )}
        >
          <InfoCircleOutlined className="payrate-bonus-info-icon" />
        </Tooltip>
      </span>
    );
  };

  const renderBody = () => {
    return (
      <div className="payrate-bonus-modal-layout">
        <Form form={form} className="payrate-bonus-modal-form">
          <Form.Item name="category" hidden>
            <input />
          </Form.Item>
          <div className="payrate-bonus-modal-title">Edit Payrate & Bonus</div>
          <div className="payrate-bonus-staff-row">
            <span>Staff</span>
            <strong>{editingStaff?.fullName}</strong>
          </div>
          <div className="payrate-bonus-section-title">Payout Settings</div>
          <div className="payrate-bonus-category-row">
            <span>Payout Category</span>
            <Radio.Group
              value={category}
              onChange={(e) => {
                const cat = e.target.value;
                setCategory(cat);
                const update = { category: cat };
                if (cat === "XRP") {
                  update.includeXeroHours = true;
                  update.xeroPay = null;
                } else if (cat === "NXRP") {
                  update.xeroPayrate = null;
                  update.xeroHours = null;
                  update.xeroPay = null;
                  update.kiwiSaver = null;
                  update.includeXeroHours = false;
                  form.setFields([
                    { name: "xeroPayrate", errors: [] },
                    { name: "xeroHours", errors: [] },
                    { name: "xeroPay", errors: [] },
                    { name: "kiwiSaver", errors: [] },
                  ]);
                }
                form.setFieldsValue(update);
              }}
            >
              <Radio value="XRP">XERO Profile (XRP)</Radio>
              <Radio value="NXRP">Cash Profile (NXRP)</Radio>
            </Radio.Group>
          </div>
          {!isCashProfile && (
            <div className="payrate-bonus-include-row">
              {renderIncludeXeroLabel()}
              <Form.Item name="includeXeroHours" valuePropName="checked" className="payrate-bonus-checkbox-field">
                <Checkbox
                  onChange={(event) => {
                    if (event.target.checked) {
                      form.setFieldValue("xeroPay", null);
                    }
                    form.setFields([
                      { name: "xeroHours", errors: [] },
                      { name: "xeroPay", errors: [] },
                    ]);
                  }}
                >
                  {/* {includeXeroHours === false ? "No" : "Yes"} */}
                  Yes
                </Checkbox>
              </Form.Item>
            </div>
          )}
          <div className="payrate-bonus-modal-grid">
            {renderNumberField({ label: "XERO Payrate", name: "xeroPayrate", disabled: xeroPayrateDisabled, suffix: "$", required: true })}
            {renderNumberField({ label: "Non-XERO Payrate", name: "nonXeroPayrate", suffix: "$", required: true })}
            {renderNumberField({ label: "XERO hours", name: "xeroHours", disabled: xeroHoursDisabled, suffix: "hrs/week", required: true })}
            {renderNumberField({ label: "KiwiSaver", name: "kiwiSaver", disabled: kiwiSaverDisabled, suffix: "$" })}
            {renderNumberField({ label: "XERO Pay", name: "xeroPay", disabled: xeroPayDisabled, suffix: "$", required: true })}
          </div>
          <div className="payrate-bonus-modal-divider" />
          <div className="payrate-bonus-section-title">Bonus Setting</div>
          <div className="payrate-bonus-modal-grid">
            {renderNumberField({ label: "Bonus Base", name: "bonusBase", suffix: "$" })}
            {renderNumberField({ label: "Fuel Support", name: "fuelSupport", suffix: "$" })}
            {renderNumberField({ label: "Bonus Step", name: "bonusStep", suffix: "$" })}
            <div />
            {renderNumberField({ label: "Bonus Rate", name: "bonusRate", suffix: "$" })}
          </div>
          <Row gutter={[30, 0]} style={{ marginTop: 34 }}>
            <Col flex="1">
              <Button className="payrate-bonus-cancel-btn" block onClick={onClose}>
                Cancel
              </Button>
            </Col>
            <Col flex="1">
              <Button type="primary" className="payrate-bonus-save-btn" block onClick={handleSave}>
                Save
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    );
  };

  // -------------------------- MAIN --------------------------
  return (
    <Modal
      open={!!editingStaff}
      title={null}
      footer={null}
      closable={false}
      centered
      width={620}
      className="payrate-bonus-edit-modal"
      onCancel={onClose}
    >
      {renderBody()}
    </Modal>
  );
};

export default PayrateBonusEditDialog;
